import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import { useDispatch } from 'react-redux';
import { importAlertState } from '../../../redux/actions/alert';
import PlanDeCuentaDropdown from '../../../containers/PlanDeCuentaDropdown/PlanDeCuentaDropdown';
import {
  GetContactAccountSuggestions,
  GetContactAccountSuggestionCounts,
  AcceptContactAccountSuggestion,
  RejectContactAccountSuggestion,
  AcceptContactAccountSuggestions,
  RejectContactAccountSuggestions,
  GenerateContactAccountSuggestions,
  GetAccountSuggestionManualStatus,
  RefreshAccountSuggestionManual,
} from '../../../graphql/accountSuggestions';
import { suggestionHasResolvableAccount } from '../../../utils/accountSuggestions';
import {
  notifyAccountSuggestionsChanged,
  subscribeAccountSuggestionsChanged,
} from '../../../utils/accountSuggestionsPanel';
import { Roles } from '../../../utils/roles';
import { UserContext } from '../../../context';
import aiIcon from '../../../assets/icons/ai-icon.svg';
import './AccountSuggestionsSection.scss';

const ITEMS_PER_PAGE_OPTIONS = [20, 50, 100];

const confidenceTone = (confidence) => {
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.6) return 'good';
  if (confidence >= 0.4) return 'mid';
  return 'low';
};

/**
 * Panel de sugerencias de cuenta contable para clientes y proveedores.
 *
 * Cada sugerencia se acepta de a una porque crea una regla que afecta todos los asientos
 * futuros del contacto. La aceptación masiva solo aplica a filas seleccionadas a mano.
 */
export const AccountSuggestionsSection = ({
  companyId,
  initialDirection = null,
  embedded = false,
  onSuggestionsChanged,
}) => {
  const { t } = useTranslation('configuracion');
  const dispatch = useDispatch();
  const userInfo = useContext(UserContext);
  const canRefreshManual = Boolean(userInfo?.roles?.includes(Roles.ADMIN));

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [isReceived, setIsReceived] = useState(initialDirection);
  const [source, setSource] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [expandedIds, setExpandedIds] = useState({});
  const [accountOverrides, setAccountOverrides] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [isBulkRunning, setIsBulkRunning] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRefreshingManual, setIsRefreshingManual] = useState(false);
  const selectAllRef = useRef(null);

  useEffect(() => {
    setIsReceived(initialDirection);
    setPage(1);
  }, [initialDirection]);

  const filter = useMemo(() => ({
    status: 'PENDING',
    page,
    limit,
    ...(typeof isReceived === 'boolean' ? { isReceived } : {}),
    ...(source ? { source } : {}),
    ...(search ? { search } : {}),
  }), [page, limit, isReceived, source, search]);

  const { data, fetching, reexecuteQuery } = GetContactAccountSuggestions(companyId, filter);
  const { data: countsData, reexecuteQuery: reexecuteCounts } = GetContactAccountSuggestionCounts(companyId);
  const { executeMutation: acceptOne } = AcceptContactAccountSuggestion();
  const { executeMutation: rejectOne } = RejectContactAccountSuggestion();
  const { executeMutation: acceptMany } = AcceptContactAccountSuggestions();
  const { executeMutation: rejectMany } = RejectContactAccountSuggestions();
  const { executeMutation: generate } = GenerateContactAccountSuggestions();
  const { data: manualStatusData, reexecuteQuery: reexecuteManualStatus } = GetAccountSuggestionManualStatus({
    pause: !canRefreshManual,
  });
  const { executeMutation: refreshManual } = RefreshAccountSuggestionManual();

  const manualStatus = manualStatusData?.accountSuggestionManualStatus;

  const items = useMemo(
    () => (data?.contactAccountSuggestions?.items || []).filter(suggestionHasResolvableAccount),
    [data?.contactAccountSuggestions?.items],
  );
  const total = data?.contactAccountSuggestions?.total || 0;
  const totalPages = data?.contactAccountSuggestions?.totalPages || 1;
  const counts = countsData?.contactAccountSuggestionCounts;

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const onSuggestionsChangedRef = useRef(onSuggestionsChanged);
  onSuggestionsChangedRef.current = onSuggestionsChanged;

  const refresh = useCallback(() => {
    reexecuteQuery({ requestPolicy: 'network-only' });
    reexecuteCounts({ requestPolicy: 'network-only' });
    setSelectedIds([]);
    onSuggestionsChangedRef.current?.();
  }, [reexecuteQuery, reexecuteCounts]);

  useEffect(() => subscribeAccountSuggestionsChanged(refresh), [refresh]);

  const notify = useCallback((content, state, duration = 4) => {
    dispatch(importAlertState({ content, state, duration }));
  }, [dispatch]);

  const handleAccept = async (item) => {
    if (busyId) return;
    setBusyId(item.id);
    try {
      const override = accountOverrides[item.id];
      const result = await acceptOne({
        companyId,
        input: {
          suggestionId: item.id,
          ...(override && override !== item.suggestedAccountKey ? { accountKeyOverride: override } : {}),
        },
      });
      const payload = result.data?.acceptContactAccountSuggestion;
      if (result.error || !payload?.status) {
        notify(result.error?.message || payload?.message || t('accountSuggestions.acceptError'), 'error', 5);
        return;
      }
      notify(t('accountSuggestions.acceptSuccess', { name: item.contactName }), 'success', 3);
      notifyAccountSuggestionsChanged();
    } catch (err) {
      notify(err?.message || t('accountSuggestions.acceptError'), 'error', 5);
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (item) => {
    if (busyId) return;
    setBusyId(item.id);
    try {
      const result = await rejectOne({ companyId, input: { suggestionId: item.id } });
      const payload = result.data?.rejectContactAccountSuggestion;
      if (result.error || !payload?.status) {
        notify(result.error?.message || payload?.message || t('accountSuggestions.rejectError'), 'error', 5);
        return;
      }
      notify(t('accountSuggestions.rejectSuccess'), 'success', 3);
      notifyAccountSuggestionsChanged();
    } catch (err) {
      notify(err?.message || t('accountSuggestions.rejectError'), 'error', 5);
    } finally {
      setBusyId(null);
    }
  };

  const handleBulk = async (accept) => {
    const ids = selectedIds;
    if (ids.length === 0) {
      notify(t('accountSuggestions.bulkEmpty'), 'warning', 4);
      return;
    }
    setIsBulkRunning(true);
    try {
      const mutation = accept ? acceptMany : rejectMany;
      const result = await mutation({ companyId, input: { suggestionIds: ids } });
      const payload = accept
        ? result.data?.acceptContactAccountSuggestions
        : result.data?.rejectContactAccountSuggestions;
      if (result.error) {
        notify(result.error.message, 'error', 5);
        return;
      }
      notify(payload?.message || t('accountSuggestions.bulkDone'), payload?.status ? 'success' : 'warning', 5);
      notifyAccountSuggestionsChanged();
    } catch (err) {
      notify(err?.message || t('accountSuggestions.bulkError'), 'error', 5);
    } finally {
      setIsBulkRunning(false);
    }
  };

  const handleGenerate = async () => {
    if (!companyId || isGenerating) return;
    setIsGenerating(true);
    try {
      const result = await generate({ companyId });
      const payload = result.data?.generateContactAccountSuggestions;
      if (result.error || !payload?.status) {
        notify(result.error?.message || payload?.message || t('accountSuggestions.generateError'), 'error', 5);
        return;
      }
      notify(payload.message, 'success', 5);
      notifyAccountSuggestionsChanged();
    } catch (err) {
      notify(err?.message || t('accountSuggestions.generateError'), 'error', 5);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRefreshManual = async () => {
    if (!canRefreshManual || isRefreshingManual) return;
    setIsRefreshingManual(true);
    try {
      const result = await refreshManual();
      const payload = result.data?.refreshAccountSuggestionManual;
      if (result.error || !payload?.status) {
        notify(result.error?.message || payload?.message || t('accountSuggestions.refreshManualError'), 'error', 6);
        return;
      }
      notify(payload.message, 'success', 6);
      reexecuteManualStatus({ requestPolicy: 'network-only' });
    } catch (err) {
      notify(err?.message || t('accountSuggestions.refreshManualError'), 'error', 6);
    } finally {
      setIsRefreshingManual(false);
    }
  };

  const formatManualUpdatedAt = (iso) => {
    if (!iso) return t('accountSuggestions.manualNeverUpdated');
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return t('accountSuggestions.manualNeverUpdated');
    }
  };

  const pageIds = useMemo(() => items.map((item) => item.id), [items]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const somePageSelected = pageIds.some((id) => selectedIds.includes(id)) && !allPageSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = somePageSelected;
    }
  }, [somePageSelected]);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAllPage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
      return;
    }
    setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const clearAccountOverride = (id) => {
    setAccountOverrides((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const renderEvidence = (item) => {
    const evidence = item.evidence || {};
    if (item.source === 'CONSENSUS') {
      return t('accountSuggestions.evidenceConsensus');
    }
    if (item.source === 'SYSTEM') {
      return t('accountSuggestions.evidenceSystemHonorarios');
    }
    return evidence.llmReason || t('accountSuggestions.evidenceLlmFallback');
  };

  const renderListContent = () => {
    if (fetching) {
      return (
        <div className="suggestions-empty suggestions-empty--loading" role="status">
          <span className="suggestions-spinner" aria-hidden />
          <p>{t('accountSuggestions.loading')}</p>
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <div className="suggestions-empty">
          <FeatherIcon icon="zap" size={36} strokeWidth={1.5} className="suggestions-empty__icon" />
          <p className="suggestions-empty__title">{t('accountSuggestions.empty')}</p>
          <p className="suggestions-empty__sub">{t('accountSuggestions.emptySub')}</p>
        </div>
      );
    }

    return (
      <div className="suggestions-list">
        {items.map((item) => {
          const isExpanded = expandedIds[item.id];
          const isBusy = busyId === item.id;
          const overrideKey = accountOverrides[item.id];
          const alternatives = item.evidence?.alternatives || [];
          const hasAccountOverride = Boolean(
            overrideKey && overrideKey !== item.suggestedAccountKey,
          );
          const confidencePct = Math.round(item.confidence * 100);

          return (
            <div key={item.id} className={`suggestion-card ${isExpanded ? 'expanded' : ''}`}>
              <div className="suggestion-row">
                <div className="suggestion-row__head">
                  <div className="suggestion-row__identity">
                    <label className="suggestion-check">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => toggleSelected(item.id)}
                      />
                    </label>

                    <div className="suggestion-contact">
                      <span className="contact-name__text">{item.contactName}</span>
                      <span className="direction-chip">
                        {item.isReceived
                          ? t('accountSuggestions.chipProvider')
                          : t('accountSuggestions.chipClient')}
                      </span>
                      {item.source === 'CONSENSUS' && (
                        <span className="suggestion-recurrent-badge" title={t('accountSuggestions.sourceRecurrent')}>
                          <FeatherIcon icon="repeat" size={12} strokeWidth={2} />
                          {t('accountSuggestions.sourceRecurrent')}
                        </span>
                      )}
                      {item.source === 'SYSTEM' && (
                        <span className="suggestion-system-badge" title={t('accountSuggestions.sourceSystemHonorarios')}>
                          <FeatherIcon icon="file-text" size={12} strokeWidth={2} />
                          {t('accountSuggestions.sourceSystemHonorarios')}
                        </span>
                      )}
                      {item.source === 'LLM' && (
                        <span className="suggestion-pascale-badge" title={t('accountSuggestions.sourceLlm')}>
                          {t('accountSuggestions.sourceLlm')}
                          <img src={aiIcon} alt="" aria-hidden />
                        </span>
                      )}
                      {item.evidence?.accountRemapped && (
                        <span
                          className="remapped-chip"
                          title={t('accountSuggestions.remappedHint', {
                            original: item.evidence.originalAccountKey,
                          })}
                        >
                          <FeatherIcon icon="repeat" size={12} strokeWidth={2} />
                          {t('accountSuggestions.remapped')}
                        </span>
                      )}
                      <button
                        type="button"
                        className={`suggestion-expand-btn ${isExpanded ? 'is-expanded' : ''}`}
                        onClick={() => toggleExpanded(item.id)}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? t('accountSuggestions.collapseDetails') : t('accountSuggestions.expandDetails')}
                        title={isExpanded ? t('accountSuggestions.collapseDetails') : t('accountSuggestions.expandDetails')}
                      >
                        <FeatherIcon icon="chevron-down" size={16} strokeWidth={2} />
                      </button>
                    </div>
                  </div>

                  <div className="suggestion-row__aside">
                    <div
                      className="suggestion-confidence"
                      title={renderEvidence(item)}
                    >
                      <div
                        className={`confidence-bar confidence-bar--${confidenceTone(item.confidence)}`}
                        role="progressbar"
                        aria-valuenow={confidencePct}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <span style={{ width: `${confidencePct}%` }} />
                      </div>
                      <span className="confidence-label">{confidencePct}%</span>
                    </div>

                    <div className="suggestion-actions">
                      <button
                        type="button"
                        className={`decision-btn accept ${isBusy ? 'is-loading' : ''}`}
                        onClick={() => handleAccept(item)}
                        disabled={Boolean(busyId)}
                        title={t('accountSuggestions.accept')}
                        aria-label={t('accountSuggestions.accept')}
                      >
                        <FeatherIcon icon="check" size={16} strokeWidth={2.5} />
                      </button>
                      <button
                        type="button"
                        className="decision-btn reject"
                        onClick={() => handleReject(item)}
                        disabled={Boolean(busyId)}
                        title={t('accountSuggestions.reject')}
                        aria-label={t('accountSuggestions.reject')}
                      >
                        <FeatherIcon icon="x" size={16} strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="suggestion-row__account">
                  <div className="suggestion-account-field">
                    <PlanDeCuentaDropdown
                      id={`suggestion-account-${item.id}`}
                      value={overrideKey || item.suggestedAccountKey}
                      onChange={(e, dropdownData) =>
                        setAccountOverrides((prev) => ({ ...prev, [item.id]: dropdownData?.value || '' }))
                      }
                      placeholder={t('accountSuggestions.accountPlaceholder')}
                      portalMenu
                      clearable={false}
                    />
                    {hasAccountOverride && (
                      <button
                        type="button"
                        className="suggestion-account-reset"
                        onClick={() => clearAccountOverride(item.id)}
                        title={t('accountSuggestions.resetAccount')}
                        aria-label={t('accountSuggestions.resetAccount')}
                      >
                        <FeatherIcon icon="x" size={16} strokeWidth={2} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className={`suggestion-details ${isExpanded ? 'expanded' : ''}`}>
                <div className="detail-section">
                  <div className="section-title">
                    <FeatherIcon icon="info" size={14} strokeWidth={2} />
                    {t('accountSuggestions.evidenceTitle')}
                  </div>
                  <p className="evidence-text">{renderEvidence(item)}</p>
                </div>

                {alternatives.length > 0 && (
                  <div className="detail-section">
                    <div className="section-title">
                      <FeatherIcon icon="shuffle" size={14} strokeWidth={2} />
                      {t('accountSuggestions.alternativesTitle')}
                    </div>
                    <div className="alternatives-list">
                      {alternatives.map((alt) => (
                        <button
                          key={alt.accountKey}
                          type="button"
                          className="alternative-chip"
                          onClick={() =>
                            setAccountOverrides((prev) => ({ ...prev, [item.id]: alt.accountKey }))
                          }
                        >
                          <span>
                            {alt.accountKey} {alt.accountName}
                          </span>
                          <span className="alternative-chip__meta">
                            {t('accountSuggestions.alternativeCompanies')}
                            {': '}
                            {alt.companiesCount}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`account-suggestions-section ${embedded ? 'is-embedded' : ''}`}>
      <div className="account-suggestions-section__head">
        <div className="suggestions-hero">
          <div className="suggestions-hero__copy">
            <div className="suggestions-hero__heading">
              <h2 className="suggestions-title">
                {embedded
                  ? t(isReceived === true
                    ? 'accountSuggestions.embeddedTitleProviders'
                    : 'accountSuggestions.embeddedTitleClients')
                  : t('accountSuggestions.title')}
              </h2>
              <span className="suggestions-hero__badge">
                <img src={aiIcon} alt="" className="pascale-logo-icon pascale-logo-icon--on-dark" aria-hidden />
                {t('accountSuggestions.aiBadge')}
              </span>
            </div>
            <p className="suggestions-subtitle">
              {embedded ? t('accountSuggestions.embeddedSubtitle') : t('accountSuggestions.subtitle')}
            </p>
            {canRefreshManual && (
              <p className="suggestions-hero__manual-meta">
                {t('accountSuggestions.manualStatus', {
                  count: manualStatus?.patternsCount ?? 0,
                  date: formatManualUpdatedAt(manualStatus?.lastRebuildAt),
                })}
              </p>
            )}
          </div>
          <div className="suggestions-hero__actions">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={isGenerating || isRefreshingManual || !companyId}
              className={`suggestions-btn suggestions-btn--primary ${isGenerating ? 'is-loading' : ''}`}
            >
              {!isGenerating && (
                <img src={aiIcon} alt="" className="pascale-logo-icon pascale-logo-icon--on-dark" aria-hidden />
              )}
              {isGenerating && <span className="suggestions-spinner suggestions-spinner--sm" aria-hidden />}
              {t('accountSuggestions.generate')}
            </button>
            {canRefreshManual && (
              <button
                type="button"
                onClick={handleRefreshManual}
                disabled={isRefreshingManual || isGenerating}
                className={`suggestions-btn suggestions-btn--secondary ${isRefreshingManual ? 'is-loading' : ''}`}
                title={t('accountSuggestions.refreshManualHint')}
              >
                {isRefreshingManual
                  ? <span className="suggestions-spinner suggestions-spinner--sm suggestions-spinner--purple" aria-hidden />
                  : <FeatherIcon icon="refresh-cw" size={15} strokeWidth={2} />}
                {t('accountSuggestions.refreshManual')}
              </button>
            )}
          </div>
        </div>

        {!embedded && (
          <div className="metrics-container">
            <div className="metrics-cards">
              <div className="metric-card">
                <span className="metric-card__value">{counts?.total ?? 0}</span>
                <span className="metric-card__label">{t('accountSuggestions.metricTotal')}</span>
              </div>
              <div className="metric-card">
                <span className="metric-card__value">{counts?.providers ?? 0}</span>
                <span className="metric-card__label">{t('accountSuggestions.metricProviders')}</span>
              </div>
              <div className="metric-card">
                <span className="metric-card__value">{counts?.clients ?? 0}</span>
                <span className="metric-card__label">{t('accountSuggestions.metricClients')}</span>
              </div>
            </div>
          </div>
        )}

        <div className="filters-section">
          <div className="suggestions-search">
            <FeatherIcon icon="search" size={15} strokeWidth={2} className="suggestions-search__icon" />
            <input
              type="search"
              className="suggestions-search__input"
              placeholder={t('accountSuggestions.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          {!embedded && (
            <select
              className="suggestions-select"
              value={typeof isReceived === 'boolean' ? String(isReceived) : ''}
              onChange={(e) => {
                const { value } = e.target;
                setIsReceived(value === '' ? null : value === 'true');
                setPage(1);
              }}
              aria-label={t('accountSuggestions.filterDirection')}
            >
              <option value="">{t('accountSuggestions.filterDirection')}</option>
              <option value="true">{t('accountSuggestions.filterProviders')}</option>
              <option value="false">{t('accountSuggestions.filterClients')}</option>
            </select>
          )}
          <select
            className="suggestions-select"
            value={source}
            onChange={(e) => {
              setSource(e.target.value || '');
              setPage(1);
            }}
            aria-label={t('accountSuggestions.filterSource')}
          >
            <option value="">{t('accountSuggestions.filterSource')}</option>
            <option value="CONSENSUS">{t('accountSuggestions.sourceConsensus')}</option>
            <option value="SYSTEM">{t('accountSuggestions.sourceSystemHonorarios')}</option>
            <option value="LLM">{t('accountSuggestions.sourceLlm')}</option>
          </select>
        </div>

        {items.length > 0 && !fetching && (
          <div className="suggestions-list-toolbar" role="toolbar" aria-label={t('accountSuggestions.selectionToolbar')}>
            <label className="suggestions-list-toolbar__select-all">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allPageSelected}
                onChange={toggleSelectAllPage}
              />
              <span>{t('accountSuggestions.selectAllPage', { count: items.length })}</span>
            </label>
            {selectedIds.length > 0 && (
              <div className="suggestions-list-toolbar__actions">
                <span className="suggestions-list-toolbar__count">
                  {t('accountSuggestions.selectedCount', { count: selectedIds.length })}
                </span>
                <button
                  type="button"
                  className="toolbar-bulk-btn reject"
                  onClick={() => handleBulk(false)}
                  disabled={isBulkRunning}
                >
                  {t('accountSuggestions.rejectSelected', { count: selectedIds.length })}
                </button>
                <button
                  type="button"
                  className="toolbar-bulk-btn accept"
                  onClick={() => handleBulk(true)}
                  disabled={isBulkRunning}
                >
                  {t('accountSuggestions.acceptSelected', { count: selectedIds.length })}
                </button>
                <button
                  type="button"
                  className="toolbar-bulk-btn clear"
                  onClick={() => setSelectedIds([])}
                  disabled={isBulkRunning}
                >
                  {t('accountSuggestions.clearSelection')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="account-suggestions-section__scroll">
        {renderListContent()}
      </div>

      {items.length > 0 && !fetching && (
        <div className="account-suggestions-section__foot">
          <div className="pagination-section">
            <span>{t('accountSuggestions.totalResults', { total })}</span>
            <div className="pagination__container">
              <select
                className="suggestions-select suggestions-select--compact"
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                aria-label="Items per page"
              >
                {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <div className="suggestions-pagination">
                <button
                  type="button"
                  className="suggestions-pagination__btn"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  <FeatherIcon icon="chevron-left" size={16} strokeWidth={2} />
                </button>
                <span className="suggestions-pagination__page">
                  {page} / {totalPages || 1}
                </span>
                <button
                  type="button"
                  className="suggestions-pagination__btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="Next page"
                >
                  <FeatherIcon icon="chevron-right" size={16} strokeWidth={2} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSuggestionsSection;
